import React, { FC, Fragment, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkFlow, DefaultSampleStep } from 'models/workflow-step';
import { Box, Select, MenuItem, Typography, Grid, SelectChangeEvent } from '@mui/material';
import PageTitle from 'components/shared/page-title/page-title.component';

type DefaultStepsProps = {
    workflow: WorkFlow;
    onDefaultStepChange: (defaultSampleStep: DefaultSampleStep) => void;
};

const DefaultSteps: FC<DefaultStepsProps> = ({ workflow, onDefaultStepChange }: DefaultStepsProps) => {
    const { t: translate } = useTranslation(['lab-settings', 'workflow']);
    const [defaultSampleStep, setDefaultSampleStep] = useState<DefaultSampleStep>({
        defaultNoSample: 'No Step',
        defaultPhysicalSample: 'No Step',
        defaultDigitalSample: 'No Step',
    });

    useEffect(() => {
        setDefaultSampleStep({
            ...workflow,
            defaultNoSample: workflow?.defaultNoSample || 'No Step',
            defaultPhysicalSample: workflow?.defaultPhysicalSample || 'No Step',
            defaultDigitalSample: workflow?.defaultDigitalSample || 'No Step',
        });
    }, [workflow]);

    const handleNoSampleChange = (event: SelectChangeEvent<string>) => {
        const sampleSteps = {
            ...defaultSampleStep,
            defaultNoSample: event.target.value,
        };
        setDefaultSampleStep(sampleSteps);
        onDefaultStepChange(sampleSteps);
    };

    const handlePhysicalSampleChange = (event: SelectChangeEvent<string>) => {
        const sampleSteps = {
            ...defaultSampleStep,
            defaultPhysicalSample: event.target.value,
        };
        setDefaultSampleStep(sampleSteps);
        onDefaultStepChange(sampleSteps);
    };

    const handleDigitalSampleChange = (event: SelectChangeEvent<string>) => {
        const sampleSteps = {
            ...defaultSampleStep,
            defaultDigitalSample: event.target.value,
        };
        setDefaultSampleStep(sampleSteps);
        onDefaultStepChange(sampleSteps);
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h3" title={translate('lab-settings:STANDARD_WORKFLOW')} />
            <Box mb={2}>{translate('STANDARD_WORKFLOW_DESC')}</Box>
            <Grid container>
                <Grid item sm={12} md={8}>
                    <Box>
                        <Typography variant="subtitle2">{translate('orders:NO_SAMPLE')}</Typography>
                        <Select
                            fullWidth
                            value={defaultSampleStep.defaultNoSample}
                            onChange={handleNoSampleChange}
                            renderValue={(value) => {
                                const item = workflow?.steps?.find(({ _id: v }) => v === value);
                                return item ? item.name : value;
                            }}
                        >
                            <MenuItem value="No Step">{translate('workflow:NO_STEP')}</MenuItem>
                            {workflow?.steps?.map((step, index) => (
                                <MenuItem value={step._id} key={index}>
                                    {step.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Box mt={2}>
                        <Typography variant="subtitle2">{translate('orders:PHYSICAL')}</Typography>
                        <Select
                            fullWidth
                            value={defaultSampleStep.defaultPhysicalSample}
                            onChange={handlePhysicalSampleChange}
                            renderValue={(value) => {
                                const item = workflow?.steps?.find(({ _id: v }) => v === value);
                                return item ? item.name : value;
                            }}
                        >
                            <MenuItem value="No Step">{translate('workflow:NO_STEP')}</MenuItem>
                            {workflow?.steps?.map((step, index) => (
                                <MenuItem value={step._id} key={index}>
                                    {step.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Box mt={2}>
                        <Typography variant="subtitle2">{translate('orders:DIGITAL')}</Typography>
                        <Select
                            fullWidth
                            value={defaultSampleStep.defaultDigitalSample}
                            onChange={handleDigitalSampleChange}
                            renderValue={(value) => {
                                const item = workflow?.steps?.find(({ _id: v }) => v === value);
                                return item ? item.name : value;
                            }}
                        >
                            <MenuItem value="No Step">{translate('workflow:NO_STEP')}</MenuItem>
                            {workflow?.steps?.map((step, index) => (
                                <MenuItem value={step._id} key={index}>
                                    {step.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Grid>
            </Grid>
        </Fragment>
    );
};

export default DefaultSteps;
